import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faqQuestionsApi } from '../../services/adminApi';

const SortableRow = ({ question, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableCellStyle = {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  const categoryBadgeStyle = {
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    display: 'inline-block'
  };

  const statusButtonStyle = (isActive) => ({
    backgroundColor: isActive ? '#dcfce7' : '#fef2f2',
    color: isActive ? '#166534' : '#dc2626',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    display: 'inline-block'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '1rem',
    cursor: 'pointer',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#dc2626',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '0',
    transition: 'color 0.2s'
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div
            {...listeners}
            style={dragHandleStyle} 
            title="Sürükleyerek sıralayın"
          >
            ⋮⋮
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
              {question.question}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.5' }}>
              {question.answer.length > 100 
                ? `${question.answer.substring(0, 100)}...` 
                : question.answer
              }
            </div>
            <div style={categoryBadgeStyle}>
              {question.category_title}
            </div>
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <span style={statusButtonStyle(question.is_active == 1)}>
          {question.is_active == 1 ? 'Aktif' : 'Pasif'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <span style={statusButtonStyle(question.show_on_homepage == 1)}>
          {question.show_on_homepage == 1 ? 'Evet' : 'Hayır'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {question.sort_order || 0}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {new Date(question.created_at).toLocaleDateString('tr-TR')}
        </div>
      </td>
      <td style={tableCellStyle}>
        <Link
          to={`/admin/faq-questions/${question.id}/edit`}
          style={actionLinkStyle}
          onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
          onMouseOut={(e) => e.target.style.color = '#2563eb'}
        >
          Düzenle
        </Link>
        <button
          onClick={() => onDelete(question.id)}
          style={actionButtonStyle}
          onMouseOver={(e) => e.target.style.color = '#b91c1c'}
          onMouseOut={(e) => e.target.style.color = '#dc2626'}
        >
          Sil
        </button>
      </td>
    </tr>
  );
};

const FaqQuestionsList = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await faqQuestionsApi.getAll();
      if (response.data.success) {
        // Check if data is array or object with data property
        const questionsData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data?.data || [];
        setQuestions(questionsData);
      } else {
        setError('FAQ soruları yüklenirken hata oluştu');
        setQuestions([]);
      }
    } catch (err) {
      console.error('Questions fetch error:', err);
      setError('FAQ soruları yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu soruyu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await faqQuestionsApi.delete(id);
      if (response.data.success) {
        await fetchQuestions();
        alert('Soru başarıyla silindi');
      } else {
        alert('Soru silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Soru silinirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = questions.findIndex(item => item.id === active.id);
      const newIndex = questions.findIndex(item => item.id === over.id);
      
      const newQuestions = arrayMove(questions, oldIndex, newIndex);
      setQuestions(newQuestions);

      try {
        // Update sort_order for all questions
        const updatePromises = newQuestions.map((question, index) => {
          return faqQuestionsApi.update(question.id, { sort_order: index + 1 });
        });

        await Promise.all(updatePromises);
        
        // Update local state with new sort_order values
        setQuestions(prevQuestions => 
          prevQuestions.map((question, index) => ({
            ...question,
            sort_order: index + 1
          }))
        );
      } catch (err) {
        console.error('Sort update error:', err);
        // Revert on error
        await fetchQuestions();
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerCardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.375rem',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    border: 'none',
    cursor: 'pointer'
  };

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '1rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '16rem',
    fontSize: '1.125rem',
    color: '#6b7280'
  };

  const errorStyle = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '0.375rem',
    textAlign: 'center'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280'
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>FAQ Soruları - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={loadingStyle}>
            <div>Yükleniyor...</div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>FAQ Soruları - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={errorStyle}>
            <span>❌</span> {error}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>FAQ Soruları - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={headerCardStyle}>
          <div style={headerStyle}>
            <h1 style={titleStyle}>FAQ Soruları</h1>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Link to="/admin/faq-categories" style={buttonStyle}>
                Kategoriler
              </Link>
              <Link to="/admin/faq-questions/create" style={buttonStyle}>
                Yeni Soru Ekle
              </Link>
            </div>
          </div>
        </div>

        <div style={tableContainerStyle}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Soru & Cevap</th>
                  <th style={tableHeaderStyle}>Durum</th>
                  <th style={tableHeaderStyle}>Anasayfa</th>
                  <th style={tableHeaderStyle}>Sıralama</th>
                  <th style={tableHeaderStyle}>Oluşturulma</th>
                  <th style={tableHeaderStyle}>İşlemler</th>
                </tr>
              </thead>
              <SortableContext
                items={questions && questions.length > 0 ? questions.map(q => q.id) : []}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {!questions || questions.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={emptyStateStyle}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❓</div>
                        <div style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                          Henüz soru yok
                        </div>
                        <div style={{ fontSize: '0.875rem' }}>
                          İlk FAQ sorunuzu oluşturmak için "Yeni Soru Ekle" butonuna tıklayın.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    questions && questions.length > 0 ? questions.map(question => (
                      <SortableRow
                        key={question.id}
                        question={question}
                        onDelete={handleDelete}
                      />
                    )) : null
                  )}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      </div>
    </>
  );
};

export default FaqQuestionsList;
